import classNames from 'classnames';
import { forwardRef } from 'react';
import type { ComponentProps, JSX, ReactNode } from 'react';

type Props = Omit<JSX.IntrinsicElements['a'], 'href'> & { disabled?: boolean };

/**
 * Render semantically a button, but use <a> tag instead
 *
 * This component should be used as little as possible. <a> should be used for rendering
 * links and <button> for clickable elements, where <button> can be styled to look like <a>.
 * This component is designed to be used for cases where styling is already provided for <a>
 * and would be difficult to specify equivalent style for a <button>, like in Bootstrap's
 * dropdown items.
 */
export const ButtonAsA = forwardRef<HTMLAnchorElement, Props>(
  ({ children, disabled, onClick, ...props }, ref) => (
    // biome-ignore lint/a11y/useValidAnchor: intentional usage
    <a
      {...props}
      className={classNames(
        // normally, text-muted sets the color we want, but in some contexts
        // (dropdown items etc), disabled does the job.
        disabled && 'text-muted disabled pointer-events-none',
        props.className,
      )}
      href='#'
      onClick={
        disabled
          ? undefined
          : (e) => {
              e.preventDefault();
              return onClick?.(e);
            }
      }
      ref={ref}
    >
      {children}
    </a>
  ),
);

ButtonAsA.displayName = 'ButtonAsA'; // because forwardRef killed it

/**
 * Shortcut to generate simple text link buttons, useful especially for locale files
 *
 * Example:
 * ```
 * // messages.tsx
 * confetti: (popConfetti: (content: React.ReactNode) => React.ReactNode) => (
 *   <>
 *     Pop some
 *     {` ${popConfetti('Confetti')}`}
 *     !!!
 *   </>
 *
 * // Confetti.tsx
 * <p>
 *   {msg.foo.bar.confetti(makeWrapWithButtonAsA(() => alert('Wooooo!!!')))}
 * </p>
 * )
 *
 * ```
 */
export const makeWrapWithButtonAsA =
  (onClick: Props['onClick'], otherProps?: ComponentProps<typeof ButtonAsA>) =>
  (content: ReactNode): JSX.Element => (
    <ButtonAsA onClick={onClick} {...otherProps}>
      {content}
    </ButtonAsA>
  );
